import classNames from 'classnames';
import type { FC, PropsWithChildren } from 'react';
import React, { use, useReducer } from 'react';

import AbbyChatLink from 'src/components/common/abby-chat/abby-chat-link';
import AnimatedExpandWrapper from 'src/components/common/animated-expand-wrapper';
import RedesignDownChevronThin from 'src/components/icons/redesign-down-chevron-thin';
import { getFooterColors } from 'src/components/layout/footer/colors';
import { CookieConsentBannerLink } from 'src/components/layout/footer/cookie-consent-banner-link';
import { SiteContext } from 'src/context/site-context';
import { openCookieConsentBanner } from 'src/cookies/cookie-consent-banner';
import useWindowSize from 'src/custom-hooks/use-window-size';
import { LinkType } from 'src/enums/link-type';
import {
  amplitudeBrandName,
  amplitudeLinkTrackEvent,
  amplitudeTrackEvent,
} from 'src/helpers/amplitude-event-track';

import * as styles from './footer-category.module.scss';

type FooterCategoryProps = {
  title: string;
  links: readonly Queries.FooterLink[];
};

const FooterCategory: FC<PropsWithChildren<FooterCategoryProps>> = ({ title, links, children }) => {
  const { styling, buildType, pageLevel, language } = use(SiteContext);
  const { isTabletOrSmaller } = useWindowSize();
  const [isOpen, toggleOpen] = useReducer((isOpen) => !isOpen, false);

  const footerColors = getFooterColors(styling, buildType);

  const handleClickButton = () => {
    void amplitudeTrackEvent(`Click to Footer Section Title`, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      title,
      ctaName: title,
      component: 'Footer Category',
    });
  };

  const handleClickLink = (name: string, path: string | null) => {
    void amplitudeLinkTrackEvent({
      buildType,
      language,
      linkName: name,
      linkPath: path,
      componentName: 'Footer Category',
      pageLevel,
      linkEventName: 'Footer Link',
    });
  };

  const buttonClick = () => {
    toggleOpen();
    handleClickButton();
  };

  return (
    <div className={styles.container}>
      {isTabletOrSmaller ? (
        <>
          <button type="button" className={styles.button} onClick={buttonClick}>
            <div className={styles.sectionTitle}>{title}</div>
            <div className={classNames(styles.chevron, isOpen && styles.open)}>
              <RedesignDownChevronThin size="1.25rem" fill={footerColors.text} />
            </div>
          </button>
        </>
      ) : (
        <div className={classNames(styles.sectionTitle, styles.desktopTitle)}>{title}</div>
      )}
      <AnimatedExpandWrapper desktopAlwaysOpen isExpanded={isOpen}>
        <ul className={styles.linksWrapper}>
          {links.map(({ type, text, url, openInNewTab }) => {
            const onClick = () => handleClickLink(text, url);
            const isCookieBanner = (type as LinkType) === LinkType.OPEN_COOKIE_CONSENT_OPTIONS;
            const isAbbyChatLink = (type as LinkType) === LinkType.OPEN_ABBY_CHAT_BOT;

            return (
              <li key={text} className={styles.link}>
                {isCookieBanner || isAbbyChatLink ? (
                  <>
                    {isCookieBanner && (
                      <CookieConsentBannerLink
                        text={text}
                        onClick={() => {
                          openCookieConsentBanner();
                          onClick();
                        }}
                      />
                    )}
                    {isAbbyChatLink && (
                      <AbbyChatLink
                        handleClick={() => {
                          onClick();
                        }}
                      >
                        {text}
                      </AbbyChatLink>
                    )}
                  </>
                ) : (
                  <a
                    href={url!}
                    target={openInNewTab ? '_blank' : undefined}
                    rel={openInNewTab ? 'noopener noreferrer' : undefined}
                    onClick={onClick}
                  >
                    {text}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </AnimatedExpandWrapper>
      {children}
    </div>
  );
};

export default FooterCategory;
